import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { photographerAPI } from '@services/photographerApi';
import type { ThemePreset } from '@/types/photographer';

type GalleryLayout = 'grid' | 'masonry' | 'carousel';

interface ThemePresetOption {
  id: ThemePreset;
  name: string;
  description: string;
  swatches: [string, string, string];
}

const THEME_PRESETS: ThemePresetOption[] = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'Timeless black & white with a clean grid.',
    swatches: ['#111111', '#ffffff', '#d1d5db'],
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Bright accent over an airy surface.',
    swatches: ['#2563eb', '#f8fafc', '#0ea5e9'],
  },
  {
    id: 'elegant',
    name: 'Elegant',
    description: 'Warm neutrals with a soft serif tone.',
    swatches: ['#7c5e3c', '#fdf6ec', '#a78b6b'],
  },
  {
    id: 'bold',
    name: 'Bold',
    description: 'High-contrast dark mode for impact.',
    swatches: ['#dc2626', '#0a0a0a', '#fbbf24'],
  },
];

const GALLERY_LAYOUTS: { id: GalleryLayout; name: string; description: string }[] = [
  { id: 'grid', name: 'Grid', description: 'Uniform rows of equal-size thumbnails.' },
  { id: 'masonry', name: 'Masonry', description: 'Pinterest-style columns honoring image aspect.' },
  { id: 'carousel', name: 'Carousel', description: 'Featured single-image showcase with strip.' },
];

const DEFAULTS = {
  primaryColor: '#111111',
  secondaryColor: '#ffffff',
  themePreset: 'classic' as ThemePreset,
  galleryLayout: 'grid' as GalleryLayout,
};

const BrandingSettings: React.FC = () => {
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const [primaryColor, setPrimaryColor] = useState(DEFAULTS.primaryColor);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULTS.secondaryColor);
  const [themePreset, setThemePreset] = useState<ThemePreset>(DEFAULTS.themePreset);
  const [galleryLayout, setGalleryLayout] = useState<GalleryLayout>(DEFAULTS.galleryLayout);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await photographerAPI.getProfile();
        if (cancelled) return;
        const b = res.data.brandingSettings;
        if (b) {
          setLogoUrl(b.logo);
          setPrimaryColor(b.primaryColor || DEFAULTS.primaryColor);
          setSecondaryColor(b.secondaryColor || DEFAULTS.secondaryColor);
          setThemePreset(b.themePreset || DEFAULTS.themePreset);
          setGalleryLayout(b.galleryLayout || DEFAULTS.galleryLayout);
        }
      } catch {
        // leave defaults in place
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be smaller than 2MB');
      return;
    }
    setUploadingLogo(true);
    try {
      const res = await photographerAPI.uploadBrandingLogo(file);
      setLogoUrl(res.data.logoUrl);
      toast.success('Logo uploaded');
    } catch {
      toast.error('Could not upload logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeLogo = async () => {
    setSaving(true);
    try {
      await photographerAPI.updateBrandingSettings({ logo: '' });
      setLogoUrl(undefined);
      toast.success('Logo removed');
    } catch {
      toast.error('Could not remove logo');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await photographerAPI.updateBrandingSettings({
        primaryColor,
        secondaryColor,
        themePreset,
        galleryLayout,
      });
      toast.success('Branding saved');
    } catch {
      toast.error('Could not save branding');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading branding…</div>;
  }

  return (
    <div className="max-w-3xl space-y-10 p-6">
      <section>
        <h2 className="text-xl font-semibold text-gray-900">Branding</h2>
        <p className="mt-1 text-sm text-gray-500">
          Customize your logo, colors, and gallery appearance. Changes apply to your public
          photo galleries.
        </p>
      </section>

      <section aria-labelledby="logo-heading">
        <h3 id="logo-heading" className="text-base font-semibold text-gray-900">
          Logo
        </h3>
        <p className="text-sm text-gray-500">
          Shown in the gallery header. PNG, JPG, WebP, or SVG up to 2MB.
        </p>
        <div className="mt-4 flex items-center gap-4">
          <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 overflow-hidden">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Current logo"
                data-testid="branding-logo-preview"
                className="h-full w-full object-contain"
              />
            ) : (
              <span className="text-xs text-gray-400">No logo</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={handleLogoChange}
              className="hidden"
              data-testid="branding-logo-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingLogo}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {uploadingLogo ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
            </button>
            {logoUrl && (
              <button
                type="button"
                onClick={removeLogo}
                disabled={saving}
                className="text-sm text-gray-500 hover:text-red-600 disabled:opacity-50"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </section>

      <section aria-labelledby="colors-heading">
        <h3 id="colors-heading" className="text-base font-semibold text-gray-900">
          Colors
        </h3>
        <p className="text-sm text-gray-500">
          Used for buttons, links, and accents in the public gallery.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ColorInput
            id="primaryColor"
            label="Primary color"
            value={primaryColor}
            onChange={setPrimaryColor}
          />
          <ColorInput
            id="secondaryColor"
            label="Secondary color"
            value={secondaryColor}
            onChange={setSecondaryColor}
          />
        </div>
      </section>

      <section aria-labelledby="theme-heading">
        <h3 id="theme-heading" className="text-base font-semibold text-gray-900">
          Gallery theme
        </h3>
        <p className="text-sm text-gray-500">
          Pick a preset CSS profile that styles the public gallery experience.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {THEME_PRESETS.map((preset) => {
            const selected = themePreset === preset.id;
            return (
              <button
                type="button"
                key={preset.id}
                data-testid={`theme-preset-${preset.id}`}
                onClick={() => setThemePreset(preset.id)}
                aria-pressed={selected}
                className={`text-left rounded-lg border p-4 transition ${
                  selected
                    ? 'border-indigo-500 ring-2 ring-indigo-200 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  {preset.swatches.map((c, i) => (
                    <span
                      key={i}
                      className="h-5 w-5 rounded-full border border-black/10"
                      style={{ backgroundColor: c }}
                      aria-hidden
                    />
                  ))}
                </div>
                <div className="mt-3 font-medium text-gray-900">{preset.name}</div>
                <div className="text-xs text-gray-500">{preset.description}</div>
              </button>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="layout-heading">
        <h3 id="layout-heading" className="text-base font-semibold text-gray-900">
          Gallery layout
        </h3>
        <p className="text-sm text-gray-500">How photos are arranged on the public gallery.</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {GALLERY_LAYOUTS.map((layout) => {
            const selected = galleryLayout === layout.id;
            return (
              <button
                type="button"
                key={layout.id}
                data-testid={`gallery-layout-${layout.id}`}
                onClick={() => setGalleryLayout(layout.id)}
                aria-pressed={selected}
                className={`text-left rounded-lg border p-4 transition ${
                  selected
                    ? 'border-indigo-500 ring-2 ring-indigo-200 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium text-gray-900">{layout.name}</div>
                <div className="mt-1 text-xs text-gray-500">{layout.description}</div>
              </button>
            );
          })}
        </div>
      </section>

      <div className="flex justify-end border-t border-gray-200 pt-6">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="branding-save"
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save branding'}
        </button>
      </div>
    </div>
  );
};

interface ColorInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const ColorInput: React.FC<ColorInputProps> = ({ id, label, value, onChange }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    <div className="mt-1 flex items-center gap-2">
      <input
        id={id}
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 cursor-pointer rounded-md border border-gray-300 bg-white"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
        aria-label={`${label} hex value`}
      />
    </div>
  </div>
);

export default BrandingSettings;
